import { Cpu, Guitar, Music2, SlidersHorizontal, WandSparkles } from 'lucide-react'
import type { BetaUsageOption, HardwareItem, LandingCopy, Language, RoadmapItem, StepItem } from '../models/LandingPage'

export const copy: Record<Language, LandingCopy> = {
  en: {
    nav: ['How it works', 'Demo', 'Hardware', 'Roadmap'], join: 'Join Beta', eyebrow: 'The AI that understands guitar tone',
    heroBadge: 'Founding Beta · Helix first',
    hero1: 'Find.', hero2: 'Analyze.', hero3: 'Play.',
    heroSub: 'Turn any song into a native preset for your guitar modeler.', watch: 'Watch Interactive Demo',
    heroExplain: 'Paste a song, discover its Tone Signature, and generate a preset designed for your hardware.',
    heroHardware: 'Helix comes first. Kemper, Quad Cortex, HeadRush, Boss and more are planned.',
    heroPrimaryCta: 'Join Founding Beta',
    trusted: 'Built for players who want to spend less time tweaking and more time playing.',
    howTitle: 'From song to stage-ready tone.', howSub: 'Five intelligent steps. One native preset you can actually use.',
    demoTitle: 'See the engine think.', demoSub: 'A product experience designed to feel simple, while the hard work happens underneath.',
    whyTitle: 'Stop chasing tones for hours.', whySub: 'ToneSignature collapses a fragmented workflow into one focused process.',
    aiTitle: 'Refine the preset by talking to it.', aiSub: 'Your instrument, speakers and playing style matter. The AI Assistant adapts the preset around your real rig.',
    hardwareTitle: 'Native presets. Not generic settings.', hardwareSub: 'We build for the hardware guitarists already own and trust.',
    roadmapTitle: 'A focused path from beta to platform.', roadmapSub: 'Every milestone expands the product without rebuilding the foundation.',
    betaTitle: 'Become a founding beta player.', betaSub: 'Help shape the first release and get early access to ToneSignature.',
    noObligation: 'Joining does NOT create any future purchase obligation.', submit: 'Request beta access', sent: 'You are on the founding beta list.',
  },
  it: {
    nav: ['Come funziona', 'Demo', 'Hardware', 'Roadmap'], join: 'Partecipa alla Beta', eyebrow: 'The AI that understands guitar tone',
    heroBadge: 'Founding Beta · Helix prima',
    hero1: 'Trova.', hero2: 'Analizza.', hero3: 'Suona.',
    heroSub: 'Trasforma qualsiasi brano in un preset nativo per il tuo guitar modeler.', watch: 'Guarda la demo interattiva',
    heroExplain: 'Incolla un brano, scopri la sua Tone Signature e genera un preset pensato per il tuo hardware.',
    heroHardware: 'Helix arriva per primo. Kemper, Quad Cortex, HeadRush, Boss e altri sono pianificati.',
    heroPrimaryCta: 'Partecipa alla Founding Beta',
    trusted: 'Pensato per chi vuole perdere meno tempo sui parametri e suonare di più.',
    howTitle: 'Dal brano a un suono pronto per il palco.', howSub: 'Cinque passaggi intelligenti. Un preset nativo davvero utilizzabile.',
    demoTitle: 'Guarda il motore al lavoro.', demoSub: 'Un’esperienza semplice in superficie, mentre il lavoro complesso avviene dietro le quinte.',
    whyTitle: 'Smetti di inseguire il suono per ore.', whySub: 'ToneSignature riunisce un flusso frammentato in un unico processo mirato.',
    aiTitle: 'Perfeziona il preset parlandoci.', aiSub: 'Strumento, casse e stile contano. L’AI Assistant adatta il preset al tuo rig reale.',
    hardwareTitle: 'Preset nativi. Non impostazioni generiche.', hardwareSub: 'Costruiamo per l’hardware che i chitarristi già possiedono e conoscono.',
    roadmapTitle: 'Un percorso chiaro dalla beta alla piattaforma.', roadmapSub: 'Ogni milestone espande il prodotto senza rifare le fondamenta.',
    betaTitle: 'Diventa un founding beta player.', betaSub: 'Aiutaci a definire la prima release e accedi in anticipo a ToneSignature.',
    noObligation: 'L’iscrizione NON crea alcun obbligo di acquisto futuro.', submit: 'Richiedi accesso beta', sent: 'Sei nella lista della founding beta.',
  }
}

export const navTargets = ['/#how', '/#demo', '/#hardware', '/#roadmap']

export const steps: StepItem[] = [
  { title: 'Paste YouTube Link', description: 'Drop in the song you want to recreate.', Icon: Music2 },
  { title: 'Separate Guitar', description: 'AI isolates the guitar from the full mix.', Icon: Guitar },
  { title: 'Analyze Tone Signature', description: 'Gain, EQ, dynamics, ambience and feel.', Icon: SlidersHorizontal },
  { title: 'Generate Native Preset', description: 'A real preset for your selected modeler.', Icon: Cpu },
  { title: 'Refine with AI Assistant', description: 'Adapt it to your guitar, cab and context.', Icon: WandSparkles },
]

export const hardware: HardwareItem[] = [
  { name: 'Line 6 Helix', value: 'Helix', status: 'Beta', description: 'Native .hlx' },
  { name: 'Kemper', status: 'Coming soon', description: 'Rig profile workflow' },
  { name: 'Quad Cortex', status: 'Coming soon', description: 'Capture-ready chain' },
  { name: 'Fractal', status: 'Planned', description: 'Axe-Fx / FM series' },
  { name: 'TONEX', status: 'Planned', description: 'Tone model workflow' },
  { name: 'Boss', status: 'Planned', description: 'GT platform' },
  { name: 'HeadRush', status: 'Planned', description: 'Prime / Core' },
]

export const betaUsageOptions: BetaUsageOption[] = [
  { label: 'Home', value: 'Home' },
  { label: 'Studio', value: 'Studio' },
  { label: 'Live', value: 'Live' },
  { label: 'Home & Studio', value: 'HomeAndStudio' },
  { label: 'Home & Live', value: 'HomeAndLive' },
  { label: 'Studio & Live', value: 'StudioAndLive' },
  { label: 'All', value: 'All' },
]

export const roadmap: RoadmapItem[] = [
  { quarter: 'Q3', label: 'Foundation', items: ['Landing & interactive demo', 'Private beta', 'Helix preset engine'] },
  { quarter: 'Q4', label: 'Intelligence', items: ['AI Assistant', 'Kemper support', 'Community feedback loop'] },
  { quarter: 'Q1', label: 'Platform', items: ['Quad Cortex support', 'Preset marketplace', 'Creator profiles'] },
]

export const oldWayItems = ['Searching presets', 'Watching tutorials', 'Changing EQ', 'Trying IRs', 'Repeat again…']
